<template>
    <div 
        id="search-result"
        class="search-result no-outline"
        tabindex="0"
        data-no-focus-scroll
        @focus="onFocus"
        @focusin="onFocusin" 
        @focusout="onFocusout"
    >
        <div> 
            <Rows-container 
                ref="rowsContainer" 
                :items-props="partLines"
                @on-down="onDownRows"
            >
                <template v-for="(item, index) in partLines" :key="item.lineId">
                    <line-view-skeleton v-if="item.cardItems === null" :name="item.skeleton" />
                    <row-view
                        v-if="checkExist(item)"
                        :ref="item.ref"
                        :index-row="index"
                        :key-data="item.keyData"
                        :item="item"
                        :items="item.cardItems"
                        :line-id="item.lineId"
                        :title="item.title"
                        @loaded-line="onLoadedLine"
                        @list-item-selected="itemsSelected"
                        @show-all="showAll"
                    />
                </template>
            </Rows-container>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { ref, type Ref, computed, watch, nextTick, type ComputedRef } from "vue";
    import { AppConstants } from "@/common";
    import { useApplicationStore } from "@/stores/Application";
    import { useFocus } from "@/composables/useFocus";
    import { storeToRefs } from 'pinia';
    import { ComponentName as SkeletonName } from "@/components/Skeleton/types";
    import { useParentControlStore } from "@/stores/ParentControl";
    import { useParentControl } from "@/composables/parentControl/useParentControl";
    import type { RowConfig, IEventSelected } from "@/components/RowView/types";
    import type { RowsContainerRef } from "@/components/RowsContainer/types";
    import { useSearchPageStore } from "@/stores/SearchPage";
    import type { ExposeInterface } from "./types";

    const { setIsHaveFocusedElement, focusElementLine, focusToHeader } = useFocus();

    const SearchPageStore = useSearchPageStore();
    const {
        getCounterLinesSearch,
        getCardsMoviesResult,
        getCardsShowsResult,
        getCardsChannelsResult,
        getCardsCreditsResult,
        getCardsLiveResult,
        getChannelsTitle,
        getLiveTitle,
        getMoviesTitle,
        getShowsTitle,
        getCreditsTitle,
    } = storeToRefs(SearchPageStore);
    const { 
        increaseCounterLinesSearch, 
    } = SearchPageStore;

    const ParentControlStoreModule = useParentControlStore();
    const {
        ShowParentModal,
        SetTargetWidgetAdultControl,
        SetIncorrectAdultPin,
    } = ParentControlStoreModule;
    const {
        getIsAdultPinCorrect: isAdultPinCorrect,
        getTargetWidgetAdultControl: targetWidgetAdultControl,
    } = storeToRefs(ParentControlStoreModule);

    const ApplicationStore = useApplicationStore();
    const {
        getIsHaveFocusedElement: isHavSelectedIdProgram,
    } = storeToRefs(ApplicationStore);

    const { setCurrentTimes, checkParentControlCard } = useParentControl();
    
    const { $navigator } = useNuxtApp();
    const emit = defineEmits(['show-all', 'list-item-selected', 'ready']);
    const rowsContainer: Ref<RowsContainerRef | null> = ref(null); 
    const name = "Search";
    const { shelfKeys, SearchCounterLines }: typeof AppConstants = AppConstants;
    const selectedItem: Ref<IEventSelected | null> = ref(null);

    const isReadyLines: Ref<boolean> = ref(false);

    const isLoadedComponents = computed((): boolean => rowsContainer.value !== null);
    const isReadyAll = computed((): boolean => isReadyLines.value && isLoadedComponents.value);
    const allLines: ComputedRef<RowConfig[]> = computed(() => {
        return [
            {
                keyData: shelfKeys.searchResultChannelsCards,
                title: getChannelsTitle.value,
                ref: shelfKeys.searchResultChannelsCards,
                lineId: shelfKeys.searchResultChannelsCards,
                skeleton: SkeletonName.PROGRAM_CARD_SKELETON,
                cardItems: getCardsChannelsResult.value,
            },
            {
                keyData: shelfKeys.searchResultLiveCards,
                title: getLiveTitle.value,
                ref: shelfKeys.searchResultLiveCards,
                lineId: shelfKeys.searchResultLiveCards,
                skeleton: SkeletonName.PROGRAM_CARD_SKELETON,
                cardItems: getCardsLiveResult.value,
            },
            {
                keyData: shelfKeys.searchResultMoviesCards,
                title: getMoviesTitle.value,
                ref: shelfKeys.searchResultMoviesCards,
                lineId: shelfKeys.searchResultMoviesCards,
                skeleton: SkeletonName.MOVIE_CARD_SKELETON,
                cardItems: getCardsMoviesResult.value,
            },
            {
                keyData: shelfKeys.searchResultShowsCards,
                title: getShowsTitle.value,
                ref: shelfKeys.searchResultShowsCards,
                lineId: shelfKeys.searchResultShowsCards,
                skeleton: SkeletonName.MOVIE_CARD_SKELETON,
                cardItems: getCardsShowsResult.value,
            },
            {
                keyData: shelfKeys.searchResultCreditsCards,
                title: getCreditsTitle.value,
                ref: shelfKeys.searchResultCreditsCards,
                lineId: shelfKeys.searchResultCreditsCards,
                skeleton: SkeletonName.MOVIE_CARD_SKELETON,
                cardItems: getCardsCreditsResult.value,
            },
        ];
    });
    const partLines: ComputedRef<RowConfig[]> = computed(() => allLines.value.slice(0, getCounterLinesSearch.value));

    const checkExist = (item: RowConfig): boolean => {
        const isExist = item.cardItems !== null && item.cardItems.length > 0;
        if (!isExist) {
            onLoadedLine();
        } 
        return isExist;
    };

    const counter = ref(1);
    const onLoadedLine = (): void => {
        if (rowsContainer.value === null) {
            return;
        }
        const countLoadedRows = rowsContainer.value.getCountLoadedRows();
        if (countLoadedRows !== null && getCounterLinesSearch.value >= countLoadedRows) {
            void nextTick(() => {
                emit('ready');
            });
        }

        if (counter.value <= SearchCounterLines) {
            const isIncreasing = (counter.value % SearchCounterLines) != 0; 
            if (isIncreasing) {
                increaseCounterLinesSearch();
                counter.value++;
            }
        }
    };

    const onListItemSelected = (): void => {
        emit("list-item-selected", selectedItem.value);
    };
    const itemsSelected = (event: IEventSelected): void => {
        selectedItem.value = event;

        checkParentControlCard(event).then((isAllow)=>{
            if (isAllow) {
                onListItemSelected();
            } else {
                ShowParentModal();
                SetTargetWidgetAdultControl(name);
            }
        }).catch((error)=> {
            Logger.captureError(error)
        });
    };

    const focusFirstRow = (): void => {
        if (rowsContainer.value) {
            rowsContainer.value.focusFirstRow();
        }
    };
    const focusElement = (): void => {
        if (isHavSelectedIdProgram.value) {
            focusElementLine();
        }
    };

    const onDownRows = (): void => {
        if (getCounterLinesSearch.value < allLines.value.length) {
            increaseCounterLinesSearch();
        }
    };

    const showAll = (event: IEventSelected): void => {
        emit("show-all", event);
    };

    const onFocus = (): void => {
        focusFirstRow();
    };
    const keyBackListener = (): void => {
        focusToHeader();
    };
    const onFocusin = (): void => {
        registerKeyListeners();
    };
    const onFocusout = (): void => {
        unregisterKeyListeners();
    };
    const registerKeyListeners = (): void => {
        $navigator.on("keypress:back", keyBackListener);
    };
    const unregisterKeyListeners = (): void => {
        $navigator.off("keypress:back", keyBackListener);
    };

    watch(
        isReadyAll, 
        (value: boolean) => {
        if (value) {
            void nextTick(()=>{
                setIsHaveFocusedElement();
                focusElement();
            });
        }
    });

    watch(
        isAdultPinCorrect, 
        (value: boolean): void => {
            if (value && name === targetWidgetAdultControl.value) {
                SetIncorrectAdultPin();
                setCurrentTimes();
                onListItemSelected();
            }
        }
    );

    defineExpose<ExposeInterface>({
        focusFirstRow
    });
</script>
